import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {
  Box,
  TextField,
  IconButton,
  Badge,
  Tooltip,
  Typography,
  SxProps,
  Stack,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import FlatInput from '../../flat-input/flat-input';
import {Modal} from '../../modal/modal';
import {LocalizedString} from '../../../backend/interfaces/localized-string';
import {BackendLanguageTag} from '../../../backend/enums';
import {titleCase} from '../../../utils/functions';

const modalSx: SxProps = {
  marginBottom: 0,
  '.MuiPaper-root': {
    width: '554px',
    borderRadius: '16px',
  },
  '& > .MuiButtonBase-root': {
    lineHeight: '24px',
    height: '36px',
    width: '77px',
    paddingX: '14px',
    borderRadius: '5px',
  },
  '& > .MuiDialogContent-root': {
    paddingBottom: 0,
  },
};

export const LocalizedStringInput = ({
  localizedValuesErrors,
  localizedInputValidation,
  defaultValueLabel,
  defaultValuePlaceholder,
  defaultValueSx,
  localizedValuesLabel,
  defaultValue,
  handleChangeDefaultValue,
  disabled,
  multiline,
  rows,
  id,
  className,
  handleBlurDefaultValue,
  handleChangeLocalizedValues,
  localizedValues,
  defaultValueRequired,
  localizedValueRequired,
  handleBlurLocalizedValues,
  defaultValueError,
  localizedValuesError,
}: {
  //--- Default value
  defaultValueLabel: string;
  defaultValuePlaceholder?: string;
  defaultValueRequired?: boolean;
  defaultValueSx?: SxProps;
  localizedValuesLabel: string;
  defaultValueError?: string;
  defaultValue: string;
  handleChangeDefaultValue: (value: string) => void;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  id?: string;
  className?: string;
  handleBlurDefaultValue?: () => void;

  //--- Localized values
  handleChangeLocalizedValues: (values: LocalizedString) => void;
  localizedValues?: LocalizedString;
  localizedValueRequired?: boolean;
  localizedValuesError?: string;
  handleBlurLocalizedValues?: () => void;
  localizedValuesErrors?: Record<keyof LocalizedString, Record<'message', string>>;
  localizedInputValidation?: (
    value: string | undefined | null,
    key?: keyof LocalizedString
  ) => void;
}) => {
  const [localizedValuesModalOpened, setLocalizedValuesModalOpened] = useState<boolean>(false);
  const [controlledLocalizedValues, setControlledLocalizedValues] = useState<LocalizedString>({
    enUS: localizedValues?.enUS || '',
    esES: localizedValues?.esES || '',
    ptPT: localizedValues?.ptPT || '',
    ptBR: localizedValues?.ptBR || '',
  });

  const {t} = useTranslation();

  const localizedInputsHaveError = useMemo(() => {
    return localizedValuesErrors && Object.entries(localizedValuesErrors).length > 0;
  }, [localizedValuesErrors]);

  const localizedValuesCount = useMemo(
    () =>
      localizedValues
        ? Object.values(localizedValues).filter((value) => value.trim().length > 0).length
        : 0,
    [localizedValues]
  );

  const tooltipTitle = useMemo(
    () =>
      localizedValuesCount > 1
        ? t(translations.localized_string_input.has_many_translations_tooltip_message, {
            count: localizedValuesCount,
            total: Object.keys(BackendLanguageTag).length,
          })
        : localizedValuesCount === 1
          ? t(translations.localized_string_input.has_one_translation_tooltip_message, {
              total: Object.keys(BackendLanguageTag).length,
            })
          : t(translations.localized_string_input.has_zero_translations_tooltip_message, {
              total: Object.keys(BackendLanguageTag).length,
            }),
    [localizedValuesCount, t]
  );

  // Handle the opened state of the localized string modal
  const handleLocalizedValuesModalOpened = (value: boolean) => {
    setLocalizedValuesModalOpened(value);
  };

  // Cancel the localized values
  const handleCancelLocalizedValues = () => {
    // Close the modal
    handleLocalizedValuesModalOpened(false);

    // Run validations
    localizedValues &&
      Object.entries(localizedValues).forEach(([language, value]) => {
        handleChangeLocalizedValue(language as keyof LocalizedString, value);
      });

    // Reset the values back
    setControlledLocalizedValues({
      enUS: localizedValues?.enUS || '',
      esES: localizedValues?.esES || '',
      ptPT: localizedValues?.ptPT || '',
      ptBR: localizedValues?.ptBR || '',
    });
  };

  // Confirm the localized values
  const handleConfirmLocalizedValues = () => {
    // Send the values back
    handleChangeLocalizedValues(controlledLocalizedValues);

    // Close the modal
    handleLocalizedValuesModalOpened(false);
  };

  // Handle the change of a localized value
  const handleChangeLocalizedValue = (
    key: keyof LocalizedString,
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Run validations
    if (localizedInputValidation) localizedInputValidation(value, key);

    // Set it
    setControlledLocalizedValues((prevState) => {
      return {
        ...prevState,
        [key]: finalValue,
      };
    });
  };

  // Handle the blur event of a localized value
  const handleBlurLocalizedValueField = (
    key: keyof LocalizedString,
    value: string | undefined | null
  ) => {
    // Run validations
    if (localizedInputValidation) localizedInputValidation(value, key);

    // Run callback
    if (handleBlurLocalizedValues) handleBlurLocalizedValues();
  };

  // Handle click on the language button
  const handleClickLanguage = () => {
    // Open the modal
    handleLocalizedValuesModalOpened(true);

    // Blur external default field value
    if (handleBlurDefaultValue) handleBlurDefaultValue();

    // Blur external default field localized value
    if (handleBlurLocalizedValues) handleBlurLocalizedValues();
  };

  return (
    <>
      <Box className={className} id={id}>
        <TextField
          placeholder={defaultValuePlaceholder}
          value={defaultValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const {value} = event.target;

            handleChangeDefaultValue(value);

            // Run external validation
            if (localizedInputValidation) localizedInputValidation(value);
          }}
          onBlur={() => {
            // Handle blur
            if (handleBlurDefaultValue) handleBlurDefaultValue();

            // Run external validation
            if (localizedInputValidation) localizedInputValidation(defaultValue);
          }}
          helperText={defaultValueError || localizedValuesError || undefined}
          error={defaultValueError || localizedValuesError ? true : false}
          disabled={disabled}
          required={defaultValueRequired}
          multiline={multiline}
          InputProps={{
            endAdornment: (
              <Tooltip title={tooltipTitle} placement="bottom">
                <IconButton
                  disabled={disabled === true ? true : false}
                  onClick={handleClickLanguage}
                  color={localizedValuesError ? 'error' : 'default'}
                >
                  <Badge
                    badgeContent={localizedValuesCount}
                    color={localizedValuesError ? 'error' : 'primary'}
                  >
                    <LanguageIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            ),
          }}
          variant="standard"
          color="neutral"
          type="text"
          sx={defaultValueSx}
          fullWidth
        />
      </Box>
      <Modal
        open={localizedValuesModalOpened}
        title={titleCase(localizedValuesLabel)}
        actions={[
          {
            label: t(translations.office.generics.cancel),
            type: 'destructive',
            onClick: handleCancelLocalizedValues,
          },
          {
            label: t(translations.office.generics.save),
            type: 'nondestructive',
            onClick: handleConfirmLocalizedValues,
            disabled: localizedInputsHaveError,
          },
        ]}
        preventCloseClick
        sx={modalSx}
      >
        <Stack gap={4} sx={{width: '100%'}}>
          {Object.entries(BackendLanguageTag).map((obj) => {
            const language = obj[0] as keyof LocalizedString;

            const errorMessage = localizedValuesErrors?.[language]?.message;

            return (
              <FlatInput
                key={`localized-string-${defaultValueLabel}-${language}`}
                label={t(translations.languages[obj[1]])}
                placeholder={t(translations.office.localized_string_input.fields.placeholder)}
                value={controlledLocalizedValues[obj[0] as keyof LocalizedString] || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeLocalizedValue(obj[0] as keyof LocalizedString, event.target.value)
                }
                onBlur={(_event) =>
                  handleBlurLocalizedValueField(
                    obj[0] as keyof LocalizedString,
                    controlledLocalizedValues[obj[0] as keyof LocalizedString] || ''
                  )
                }
                multiline={multiline}
                errors={errorMessage}
                required={localizedValueRequired}
                rows={rows || 2}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            );
          })}
        </Stack>

        {localizedValuesError && (
          <Typography variant="caption" color="error" component="div">
            {localizedValuesError}
          </Typography>
        )}
      </Modal>
    </>
  );
};
